// src/client/renderer/vfx/tile/soul-anchor-ring.ts
//
// Plan 16, Task 3.2 — Soul anchor claim ring pool.
//
// Triggered by SoulAnchorClaimEvent. Renders an expanding ring (RingGeometry)
// centred on the claimed soul_anchor tile, conveying "territory claimed".
//
// Geometry choice: RingGeometry(innerRadius=0.5, outerRadius=1.0, thetaSegments=48).
//   Unit-radius build means scale 0→1.5 drives the visible expansion.
//   Flat on XZ plane (mesh.rotation.x = -PI/2).
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   0.000–0.600s  scale 0 → 1.5      (proxy drives mesh.scale.set uniformly)
//   0.000–0.600s  opacity 1 → 0      (proxy drives material.opacity)
// Total lifetime: 600ms. One-shot — isFinished set via tl.call(fn, undefined, 0.6)
// (NOT eventCallback — see gsap-registry.ts VIRTUAL_DURATION note).
//
// Max-active: 8 (soul_anchor tiles can chain together, producing simultaneous rings).
//
// Material: MeshBasicMaterial — color 0xc063ff (matches AMBIENT_GLOW_COLORS.soul_anchor
// from Task 2.1), transparent, AdditiveBlending, depthWrite: false.
//
// Resolver design (parallel to resolveApproachHighlightForEvent from Task 2.2):
//   resolveSoulAnchorRingForEvent(event, getPositionForNode?)
//   - Returns null if event.type !== 'soul_anchor_claim'.
//   - Returns null if getPositionForNode is absent or returns undefined for event.nodeId.
//   - Returns spawn args for event.nodeId only (the primary anchor).
//   Plan 19 owns position resolution and will fan additional rings via
//   manager.spawn() using event.connectedNodeIds directly.
//
// Forward-defense F4: pool.dispose() iterates active emitters, kills timelines,
//   frees per-emitter materials, then disposes shared geometry once. Idempotent.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

const SCALE_START = 0.0;
const SCALE_END = 1.5;
const LIFETIME = 0.6; // seconds (600ms)

/** Soul anchor purple — matches AMBIENT_GLOW_COLORS.soul_anchor from Task 2.1. */
const RING_COLOR = 0xc063ff;

/** Max concurrent active emitters (anchors can chain). */
const MAX_ACTIVE = 8;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (RingGeometry — unit-radius, scaled at runtime)
// ─────────────────────────────────────────────────────────────────────────────

const RING_INNER_RADIUS = 0.5;
const RING_OUTER_RADIUS = 1.0;
const RING_THETA_SEGMENTS = 48;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface SoulAnchorRingPoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createSoulAnchorRingPool({
  scene,
  registry,
}: SoulAnchorRingPoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.RingGeometry(
    RING_INNER_RADIUS,
    RING_OUTER_RADIUS,
    RING_THETA_SEGMENTS,
  );

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Per-emitter material clone (additive blend — bright purple glow on any background)
    const material = new THREE.MeshBasicMaterial({
      color: RING_COLOR,
      transparent: true,
      opacity: 1.0,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);

    // Lie flat on the XZ tile surface
    mesh.rotation.x = -Math.PI / 2;

    // Start at zero scale
    mesh.scale.set(SCALE_START, SCALE_START, SCALE_START);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('soul_anchor_ring');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Tween 1: scale 0 → 1.5 over 600ms (proxy drives mesh.scale uniformly)
    const scaleProxy = { value: SCALE_START };
    tl.to(
      scaleProxy,
      {
        value: SCALE_END,
        duration: LIFETIME,
        ease: 'power2.out',
        onUpdate: () => {
          mesh.scale.set(scaleProxy.value, scaleProxy.value, scaleProxy.value);
        },
      },
      0,
    );

    // Tween 2: opacity 1 → 0 over 600ms (proxy drives material.opacity)
    const opacityProxy = { value: 1.0 };
    tl.to(
      opacityProxy,
      {
        value: 0.0,
        duration: LIFETIME,
        ease: 'power2.in',
        onUpdate: () => {
          material.opacity = opacityProxy.value;
        },
      },
      0,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method returns the
  // current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees ALL GPU resources
  // WITHOUT expecting prior release() calls. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `SoulAnchorClaimEvent` to soul anchor ring spawn args.
 *
 * Returns null for:
 *   - any non-soul_anchor_claim event
 *   - soul_anchor_claim when `getPositionForNode` is not provided
 *   - soul_anchor_claim when `getPositionForNode(event.nodeId)` returns undefined
 *
 * Design choice (parallel to resolveApproachHighlightForEvent from Task 2.2):
 *   Position lookup for node→world-pos is owned by Plan 19. The resolver
 *   accepts an optional callback; when absent or returning undefined, it
 *   returns null. This lets the resolver participate in the standard event
 *   bus without hard-coupling to the board map.
 *
 *   The resolver returns spawn args for `event.nodeId` only (the primary
 *   anchor). Plan 19 will fan additional rings for `event.connectedNodeIds`
 *   by calling `manager.spawn('soul_anchor_ring', ...)` directly.
 */
export function resolveSoulAnchorRingForEvent(
  event: GameEvent,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'soul_anchor_ring'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'soul_anchor_claim') return null;

  if (!getPositionForNode) return null;

  const position = getPositionForNode(event.nodeId);
  if (!position) return null;

  return {
    poolId: 'soul_anchor_ring',
    args: { position },
  };
}
